import React, { useRef, useState } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { ChevronLeft, ChevronRight, Star, ArrowRight } from 'lucide-react'

const RAW_CRUISES    = Array.isArray(window.wpData?.cruises) ? window.wpData.cruises : []
const CRUISES_INTRO  = window.wpData?.cruisesIntro ?? ''
const BASE           = (window.wpData?.siteUrl ?? '').replace(/\/$/, '')
const SITE_HOST = (window.wpData?.siteUrl ?? '')
  ? new URL(window.wpData.siteUrl).hostname
  : window.location.hostname

const BRAND_LABELS = {
  ljn:    'Le Journey Cruises',
  amanda: 'Amanda Cruises',
  hera:   'Hera Cruises',
}

function StarRow({ count }) {
  const n = Math.min(5, Math.max(0, parseInt(count) || 0))
  if (!n) return null
  return (
    <div className="crs2-stars">
      {Array.from({ length: n }, (_, i) => (
        <Star key={i} size={10} strokeWidth={0} fill="var(--color-gold)" color="var(--color-gold)" />
      ))}
    </div>
  )
}

export default function CruisesSection() {
  const total = RAW_CRUISES.length
  if (!total) return null

  const sliderRef = useRef(null)
  const [active, setActive] = useState(0)

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 520,
    slidesToShow: 5,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    beforeChange: (_, next) => setActive(next),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 640,  settings: { slidesToShow: 2, centerMode: false } },
    ],
  }

  return (
    <section className="hp-section hp-section--offwhite crs2-section">
      <div className="hp-inner">
        <div className="crs2-header">
          <div className="crs2-header__left">
            <div className="hp-eyebrow">Our Fleet</div>
            <h2 className="hp-title">Choose Your Journey</h2>
            <div className="hp-divider" />
            <a href={`${BASE}/about-us`} className="ljn-btn-secondary crs2-header__btn">
              About Us <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </div>
          {CRUISES_INTRO && (
            <div
              className="crs2-header__right crs2-intro"
              dangerouslySetInnerHTML={{ __html: CRUISES_INTRO }}
            />
          )}
        </div>
      </div>

      <div className="hp-inner crs2-outer">
        <Slider ref={sliderRef} {...settings}>
          {RAW_CRUISES.map((cruise, i) => {
            const cabinNum = parseInt(cruise.cabins) || 0
            const inner = (
              <>
                <div className="crs2-card__img-wrap">
                  {cruise.thumb
                    ? <img src={cruise.thumb} alt={cruise.title} className="crs2-card__img" />
                    : <div className="crs2-card__img-fallback" />
                  }
                </div>
                <div className="crs2-card__body">
                  <div className="crs2-card__brand">{BRAND_LABELS[cruise.brand] ?? cruise.brand}</div>
                  <div className="crs2-card__title">{cruise.title}</div>
                  <div className="crs2-card__meta">
                    <StarRow count={cruise.stars} />
                    {cabinNum > 0
                      ? <span className="crs2-card__cabins">{cabinNum} Cabins</span>
                      : <span className="crs2-card__cabins crs2-card__cabins--day">Day Cruise</span>
                    }
                  </div>
                </div>
              </>
            )
            return (
              <div key={i} className="crs2-slide">
                <div className="crs2-card">
                  {cruise.url
                    ? (() => {
                        let isExternal = false
                        try { isExternal = new URL(cruise.url).hostname !== SITE_HOST } catch {}
                        return (
                          <a
                            href={cruise.url}
                            className="crs2-card__link"
                            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >{inner}</a>
                        )
                      })()
                    : inner
                  }
                </div>
              </div>
            )
          })}
        </Slider>

        <div className="crs2-ctl">
          <button className="crs2-arrow" onClick={() => sliderRef.current?.slickPrev()} aria-label="Previous">
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="crs2-dots">
            {RAW_CRUISES.map((_, i) => (
              <button
                key={i}
                className={`crs2-dot${i === active ? ' crs2-dot--active' : ''}`}
                onClick={() => sliderRef.current?.slickGoTo(i)}
                aria-label={`Cruise ${i + 1}`}
              />
            ))}
          </div>
          <button className="crs2-arrow" onClick={() => sliderRef.current?.slickNext()} aria-label="Next">
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  )
}
